import React, {FC, useCallback, useEffect, useState} from 'react'
import {PageTitle} from '../../../_metronic/layout/core'
import {getDashboardMetrics, DashboardMetrics} from './api/dashboard-api'
import MetricTile from './components/MetricTile'

type TimeRange = 'today' | '7d' | '30d' | 'custom'
function utcForSydneyTime(year: number, month: number, day: number, hour = 0, minute = 0): Date {
  const approxUtc = new Date(Date.UTC(year, month - 1, day, hour - 10, minute))
  const tzPart =
    new Intl.DateTimeFormat('en-AU', {
      timeZone: 'Australia/Sydney',
      timeZoneName: 'shortOffset',
    })
      .formatToParts(approxUtc)
      .find((p) => p.type === 'timeZoneName')?.value ?? 'GMT+10'
  const match = tzPart.match(/GMT([+-])(\d+)(?::(\d+))?/)
  const sign = match?.[1] === '-' ? -1 : 1
  const offsetMinutes = sign * (Number(match?.[2] ?? 10) * 60 + Number(match?.[3] ?? 0))
  return new Date(Date.UTC(year, month - 1, day, hour, minute) - offsetMinutes * 60_000)
}

function startOfTodaySydney(): Date {
  const now = new Date()
  const parts = new Intl.DateTimeFormat('en-AU', {
    timeZone: 'Australia/Sydney',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now)
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? '0')
  return utcForSydneyTime(get('year'), get('month'), get('day'), 0, 0)
}

function sydneyStartOfDay(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number)
  return utcForSydneyTime(y, m, d, 0, 0)
}

function sydneyEndOfDay(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number)
  const nextDayUtc = new Date(Date.UTC(y, m - 1, d + 1))
  const nextParts = new Intl.DateTimeFormat('en-AU', {
    timeZone: 'Australia/Sydney',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(nextDayUtc)
  const get2 = (t: string) => Number(nextParts.find((p) => p.type === t)?.value ?? '0')
  return new Date(utcForSydneyTime(get2('year'), get2('month'), get2('day'), 0, 0).getTime() - 1)
}

function toUtcRange(
  range: TimeRange,
  customFrom?: string | null,
  customTo?: string | null
): {from: string; to: string} {
  const now = new Date()
  const to = now.toISOString()
  switch (range) {
    case 'today':
      return {from: startOfTodaySydney().toISOString(), to}
    case '7d':
      return {from: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString(), to}
    case '30d':
      return {from: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString(), to}
    case 'custom':
      return {
        from: sydneyStartOfDay(customFrom!).toISOString(),
        to: sydneyEndOfDay(customTo!).toISOString(),
      }
  }
}

const DashboardPage: FC = () => {
  const [timeRange, setTimeRange] = useState<TimeRange>('7d')
  const [customFrom, setCustomFrom] = useState<string | null>(null)
  const [customTo, setCustomTo] = useState<string | null>(null)
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null)
  const [loading, setLoading] = useState(false)
  const [hasError, setHasError] = useState(false)

  const fetchMetrics = useCallback(async () => {
    if (timeRange === 'custom' && (!customFrom || !customTo)) return

    setLoading(true)
    setHasError(false)

    try {
      const {from, to} = toUtcRange(timeRange, customFrom, customTo)
      const response = await getDashboardMetrics(from, to)
      setMetrics(response.data)
    } catch {
      setHasError(true)
    } finally {
      setLoading(false)
    }
  }, [timeRange, customFrom, customTo])

  useEffect(() => {
    fetchMetrics()
  }, [fetchMetrics])

  const isEmpty =
    metrics !== null &&
    !hasError &&
    metrics.profilesCreated === 0 &&
    metrics.submittedApplications === 0 &&
    metrics.submissionErrors === 0 &&
    metrics.otpFailures === 0 &&
    metrics.incompleteSteps === 0

  return (
    <div>
      {/* Time range selector */}
      <div className='card mb-6'>
        <div className='card-body d-flex align-items-center justify-content-between py-4 px-6'>
          <div>
            <span className='fw-semibold text-dark'>Time Range</span>
            <div className='text-muted fs-8 mt-1'>All times AU local</div>
          </div>
          <select
            className='form-select form-select-sm w-200px'
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value as TimeRange)}
          >
            <option value='today'>Today</option>
            <option value='7d'>Last 7 days</option>
            <option value='30d'>Last 30 days</option>
            <option value='custom'>Custom</option>
          </select>
        </div>
      </div>

      {/* Custom date inputs */}
      {timeRange === 'custom' && (
        <div className='d-flex gap-3 mb-6 flex-wrap align-items-center justify-content-end'>
          <div>
            <label className='form-label fs-7 mb-1'>From</label>
            <input
              type='date'
              className='form-control form-control-sm'
              onChange={(e) => setCustomFrom(e.target.value || null)}
            />
          </div>
          <div>
            <label className='form-label fs-7 mb-1'>To</label>
            <input
              type='date'
              className='form-control form-control-sm'
              onChange={(e) => setCustomTo(e.target.value || null)}
            />
          </div>
          <button
            type='button'
            className='btn btn-sm btn-primary mt-5'
            onClick={fetchMetrics}
            disabled={!customFrom || !customTo}
          >
            Apply
          </button>
        </div>
      )}

      {/* Error banner — tiles stay visible below */}
      {hasError && (
        <div className='alert alert-danger d-flex align-items-center gap-3 mb-5'>
          <span>Metrics unavailable. Try again later.</span>
          <button type='button' className='btn btn-sm btn-danger' onClick={fetchMetrics}>
            Retry
          </button>
        </div>
      )}

      {loading && (
        <div className='text-center py-10'>
          <span className='spinner-border spinner-border-sm text-primary' />
        </div>
      )}

      {/* Metric tiles — visible during error (shows — or last-fetched values) */}
      {!loading && !isEmpty && (
        <>
          <h5 className='fw-bold text-dark mb-4'>Key Metrics</h5>
          <div className='row g-4 mb-6'>
            <div className='col-sm-6 col-xl-3'>
              <MetricTile
                label='Profiles Created'
                value={metrics?.profilesCreated ?? '—'}
                subtitle='Renter profiles created'
                icon='fas fa-users'
                iconColor='bg-light-warning'
              />
            </div>
            <div className='col-sm-6 col-xl-3'>
              <MetricTile
                label='Submitted Applications'
                value={metrics?.submittedApplications ?? '—'}
                subtitle='Sent to property managers'
                icon='fas fa-tasks'
                iconColor='bg-light-warning'
              />
            </div>
            <div className='col-sm-6 col-xl-3'>
              <MetricTile
                label='Completion Rate'
                value={metrics ? `${metrics.completionRate}%` : '—'}
                subtitle='Submitted vs inactive applications'
                icon='fas fa-check-circle'
                iconColor='bg-light-success'
              />
            </div>
            <div className='col-sm-6 col-xl-3'>
              <MetricTile
                label='Draft vs Completed'
                value={metrics ? `${metrics.draftCount} : ${metrics.completedCount}` : '—'}
                subtitle='Application contexts by status'
                icon='fas fa-file-alt'
                iconColor='bg-light-primary'
              />
            </div>
          </div>

          <h5 className='fw-bold text-dark mb-4'>Error & Status Metrics</h5>
          <div className='row g-4 mb-6'>
            <div className='col-sm-6 col-xl-4'>
              <MetricTile
                label='Submission Errors'
                value={metrics?.submissionErrors ?? '—'}
                subtitle='Invalid address or format mismatches'
                icon='fas fa-exclamation-triangle'
                iconColor='bg-light-danger'
                tooltip='Count of events where property submission failed (server-side failure or invalid request) within the selected date window.'
              />
            </div>
            <div className='col-sm-6 col-xl-4'>
              <MetricTile
                label='OTP Failures'
                value={metrics?.otpFailures ?? '—'}
                subtitle='Failed identity verification attempts'
                icon='fas fa-times-circle'
                iconColor='bg-light-warning'
                tooltip='Counts OTP verification and OTP send/resend failure events within the selected date window, including wrong or expired codes, locked or lockout attempts, resend cooldown blocks, and provider send failures.'
              />
            </div>
            <div className='col-sm-6 col-xl-4'>
              <MetricTile
                label='Incomplete Steps'
                value={metrics?.incompleteSteps ?? '—'}
                subtitle='Drafts inactive for at least 24 hours'
                icon='fas fa-clock'
                iconColor='bg-light-warning'
                tooltip='Counts draft application sessions in the selected date window where no progress has been saved for 24 hours or more. Submitted and closed sessions are excluded.'
              />
            </div>
          </div>
        </>
      )}

      {/* Empty state — only when API returned data but all zeros, and no error */}
      {!loading && isEmpty && !hasError && (
        <div className='text-center text-muted py-10'>
          <p className='mb-3'>No data for this period.</p>
          <button
            type='button'
            className='btn btn-sm btn-secondary'
            onClick={() => setTimeRange('7d')}
          >
            Clear filters
          </button>
        </div>
      )}

      <div className='alert alert-info mt-5'>
        <strong>Note:</strong> Metrics are displayed only — no alerts, notifications, or support
        prompts. Intent is to observe general trends, not intervene in live workflows.
      </div>
    </div>
  )
}

const DashboardWrapper: FC = () => {
  return (
    <>
      <PageTitle breadcrumbs={[]}>Dashboard</PageTitle>
      <DashboardPage />
    </>
  )
}

export {DashboardWrapper}
